import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sparkles, Eye, Camera, Heart, Users, MessageCircle, Star, Award, Crown, CheckCircle, Play, Mic, Globe } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ScrollProgress } from '../components/ScrollProgress';
import { LanguageProvider } from '../context/LanguageContext';
export function InfluencersPage() {
  return <LanguageProvider data-id="element-1035">
      <div className="relative bg-black text-white overflow-x-hidden" data-id="element-1036">
        <ScrollProgress data-id="element-1037" />
        <Navbar data-id="element-1038" />
        <main className="w-full" data-id="element-1039">
          <InfluencerHero data-id="element-1040" />
          <WhyHostSection data-id="element-1041" />
          <ContentOpportunitiesSection data-id="element-1042" />
          <CollaborationPackagesSection data-id="element-1043" />
          <ExperienceDetailsSection data-id="element-1044" />
          <SocialProofSection data-id="element-1045" />
          <InfluencerCTA data-id="element-1046" />
        </main>
        <Footer data-id="element-1047" />
      </div>
    </LanguageProvider>;
}
function InfluencerHero() {
  return <section className="relative w-full min-h-screen bg-black flex items-center justify-center overflow-hidden" data-id="element-1048">
      <div className="absolute inset-0" data-id="element-1049">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pxKq2k9j3qcBE1TheAWARz/Experience_image.png" alt="Life in the Sikh Kingdoms" className="w-full h-full object-cover opacity-30" data-id="element-1050" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" data-id="element-1051"></div>
      </div>

      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto" data-id="element-1052">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.1
      }} data-id="element-1053">
          <Link to="/" className="inline-flex items-center gap-2 text-amber-400/70 hover:text-amber-400 text-sm tracking-wider uppercase font-light mb-12 transition-colors" data-id="element-1054">
            <ArrowLeft className="w-4 h-4" data-id="element-1055" />
            Back to Life in the Sikh Kingdoms
          </Link>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1,
        delay: 0.2
      }} data-id="element-1056">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6 font-light" data-id="element-1057">
            Presented by Akal Steel
          </p>
          <p className="text-amber-400 tracking-[0.3em] text-xs md:text-sm uppercase mb-8 font-light" data-id="element-1058">
            Host an Exclusive Experience
          </p>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white mb-8 tracking-tight leading-none" data-id="element-1059">
            HOST AN
            <br data-id="element-1060" />
            <span className="text-amber-400" data-id="element-1061">EXPERIENCE</span>
          </h1>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1062"></div>
          <p className="text-xl md:text-2xl text-white/70 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-1063">
            Create Unforgettable Content at the Intersection of Culture, Cuisine
            &amp; Immersive Art
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.6
      }} className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8 text-white/60" data-id="element-1064">
          <div className="text-center" data-id="element-1065">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1066">
              360°
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1067">Immersive</p>
            <p className="text-xs font-light" data-id="element-1068">Projections</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1069"></div>
          <div className="text-center" data-id="element-1070">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1071">
              Royal
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1072">Punjabi</p>
            <p className="text-xs font-light" data-id="element-1073">Cuisine</p>
          </div>
          <div className="hidden sm:block w-px h-12 bg-white/20" data-id="element-1074"></div>
          <div className="text-center" data-id="element-1075">
            <p className="text-sm tracking-[0.2em] uppercase mb-1 text-amber-400" data-id="element-1076">
              200+
            </p>
            <p className="font-serif text-xl text-white" data-id="element-1077">Years of</p>
            <p className="text-xs font-light" data-id="element-1078">Sikh Heritage</p>
          </div>
        </motion.div>
      </div>

      <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity
    }} data-id="element-1079">
        <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/50 to-transparent" data-id="element-1080"></div>
      </motion.div>
    </section>;
}
function WhyHostSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1081">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1082"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1083">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1084">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1085">
            Why This Experience
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1086">
            WHY <span className="text-amber-400" data-id="element-1087">HOST HERE</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1088"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-1089">
            Life in the Sikh Kingdoms is unlike any venue you've hosted in
            before. It's a living, breathing world — and your audience will feel
            every second of it.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-1090">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1091">
            <Camera className="w-8 h-8 text-amber-400 mb-4" data-id="element-1092" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1093">
              Unmatched Visual Content
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1094">
              360° immersive projections, royal cuisine, and interactive
              technology create content that stops the scroll. Every corner of
              this experience is a frame-worthy moment — from the sensory
              battlefield to the royal court audience.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1095">
            <Star className="w-8 h-8 text-amber-400 mb-4" data-id="element-1096" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1097">
              Exclusive Access
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1098">
              Behind-the-scenes creator previews, private photo moments, and
              early access to the experience before public launch. You'll have
              content no one else has — and the story to match.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1099">
            <Mic className="w-8 h-8 text-amber-400 mb-4" data-id="element-1100" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1101">
              Authentic Storytelling
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1102">
              Connect your audience with 200+ years of Sikh heritage through
              taste, sight, sound, and storytelling. This isn't a sponsored post
              — it's a genuine cultural journey your community will thank you
              for sharing.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="border-2 border-amber-400/30 p-8 bg-black/40" data-id="element-1103">
            <Heart className="w-8 h-8 text-amber-400 mb-4" data-id="element-1104" />
            <h3 className="font-serif text-2xl text-amber-400 mb-3" data-id="element-1105">
              Community Engagement
            </h3>
            <p className="text-white/70 font-light leading-relaxed" data-id="element-1106">
              Align with cultural preservation, youth education, and
              multicultural excellence. A portion of proceeds supports the Sikh
              Art and Culture Gallery at the ROM — giving your audience a reason
              to feel good about attending.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ContentOpportunitiesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1107">
      <div className="container mx-auto px-4" data-id="element-1108">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1109">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1110">
            What You'll Capture
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1111">
            CONTENT <span className="text-amber-400" data-id="element-1112">OPPORTUNITIES</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1113"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto font-light" data-id="element-1114">
            Three distinct content worlds, each more extraordinary than the
            last.
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto space-y-8" data-id="element-1115">
          {/* Immersive Projections */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-1116">
            <div className="aspect-video lg:aspect-auto relative" data-id="element-1117">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rru8mxg1f864Swgj1TtDBp/Gemini_Generated_Image_xf598lxf598lxf59.png" alt="Royal Procession" className="w-full h-full object-cover" data-id="element-1118" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" data-id="element-1119"></div>
            </div>
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center" data-id="element-1120">
              <div className="flex items-center gap-3 mb-4" data-id="element-1121">
                <Eye className="w-6 h-6 text-amber-400" data-id="element-1122" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-1123">
                  Visual Content
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-1124">
                360° Immersive Projections
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-1125">
                Maharaja Ranjit Singh's royal procession, the Khalsa cavalry,
                twilight at the palace — all rendered in breathtaking 360°
                projection across walls, floors, and tables. Every angle is a
                hero shot.
              </p>
              <div className="space-y-2" data-id="element-1126">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1127">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1128"></span>
                  Royal procession visuals
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1129">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1130"></span>
                  Khalsa army panoramas
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1131">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1132"></span>
                  Palace architecture &amp; twilight scenes
                </div>
              </div>
            </div>
          </motion.div>

          {/* Culinary Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-1133">
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center order-2 lg:order-1" data-id="element-1134">
              <div className="flex items-center gap-3 mb-4" data-id="element-1135">
                <Crown className="w-6 h-6 text-amber-400" data-id="element-1136" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-1137">
                  Food Content
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-1138">
                Royal Punjabi Cuisine
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-1139">
                Historically researched dishes from Maharaja Ranjit Singh's
                court — styled, plated, and served in an immersive setting. Chef
                interactions, dish reveals, and the story behind every course.
              </p>
              <div className="space-y-2" data-id="element-1140">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1141">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1142"></span>
                  Styled royal dish reveals
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1143">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1144"></span>
                  Chef interactions &amp; behind-the-scenes
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1145">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1146"></span>
                  5–7 course immersive dining journey
                </div>
              </div>
            </div>
            <div className="aspect-video lg:aspect-auto relative order-1 lg:order-2" data-id="element-1147">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/2zSbggEuZfU5JNU7W478R9/Gemini_Generated_Image_36dj2y36dj2y36dj.png" alt="Royal Cuisine" className="w-full h-full object-cover" data-id="element-1148" />
              <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/40" data-id="element-1149"></div>
            </div>
          </motion.div>

          {/* Interactive Moments */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-0 border-2 border-amber-400/30 overflow-hidden" data-id="element-1150">
            <div className="aspect-video lg:aspect-auto relative" data-id="element-1151">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7baikykwbzRgddsVg9e56P/Gemini_Generated_Image_k59hd9k59hd9k59h.png" alt="Interactive Experience" className="w-full h-full object-cover" data-id="element-1152" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/40" data-id="element-1153"></div>
            </div>
            <div className="p-8 md:p-10 bg-black/60 flex flex-col justify-center" data-id="element-1154">
              <div className="flex items-center gap-3 mb-4" data-id="element-1155">
                <Play className="w-6 h-6 text-amber-400" data-id="element-1156" />
                <p className="text-amber-400 text-xs tracking-[0.2em] uppercase font-light" data-id="element-1157">
                  Interactive Moments
                </p>
              </div>
              <h3 className="font-serif text-3xl text-white mb-4" data-id="element-1158">
                Sensory &amp; Interactive Technology
              </h3>
              <p className="text-white/70 font-light leading-relaxed mb-4" data-id="element-1159">
                Voice-activated royal court audiences, sensory battlefields with
                scent triggers and spatial audio, soaring eagle perspectives —
                moments your audience will watch on repeat.
              </p>
              <div className="space-y-2" data-id="element-1160">
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1161">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1162"></span>
                  Sensory battlefield experience
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1163">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1164"></span>
                  Voice-activated royal court audience
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm font-light" data-id="element-1165">
                  <span className="w-1 h-1 rounded-full bg-amber-400" data-id="element-1166"></span>
                  Soaring eagle perspectives
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function CollaborationPackagesSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1167">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1168"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1169">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-1170">
          <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1171">
            How We Work Together
          </p>
          <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1172">
            COLLABORATION <span className="text-amber-400" data-id="element-1173">PACKAGES</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1174"></div>
          <p className="text-white/70 font-light max-w-2xl mx-auto" data-id="element-1175">
            From intimate creator previews to full cultural ambassador
            partnerships, we have a collaboration that fits your vision.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1176">
          {/* Creator Preview */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0
        }} data-id="element-1177">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-1178">
              <div className="text-center mb-6" data-id="element-1179">
                <Camera className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1180" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1181">
                  Creator Preview
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-1182">
                  Early Access
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1183"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1184">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1185">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1186"></span>
                  Early access preview night before public launch
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1187">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1188"></span>
                  Behind-the-scenes content creation time
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1189">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1190"></span>
                  2 complimentary tickets
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1191">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1192"></span>
                  Social media cross-promotion
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1193">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1194"></span>
                  Digital press kit &amp; assets
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20a%20Creator%20Preview%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1195">
                Apply Now
              </motion.a>
            </div>
          </motion.div>

          {/* Experience Host */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="relative" data-id="element-1196">
            <div className="absolute top-0 left-0 right-0 flex justify-center z-30 transform -translate-y-1/2" data-id="element-1197">
              <div className="bg-amber-400 text-black font-bold py-2 px-6 flex items-center gap-2" data-id="element-1198">
                <Star className="w-4 h-4" fill="currentColor" data-id="element-1199" />
                <span className="text-xs uppercase tracking-wider" data-id="element-1200">
                  Most Popular
                </span>
              </div>
            </div>
            <div className="border-2 border-amber-400 bg-black/80 p-8 pt-12 h-full flex flex-col" data-id="element-1201">
              <div className="text-center mb-6" data-id="element-1202">
                <Users className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1203" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1204">
                  Experience Host
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-1205">
                  Private Table
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1206"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1207">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1208">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1209"></span>
                  Private hosted table for your community (10 guests)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1210">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1211"></span>
                  Dedicated content creation time &amp; access
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1212">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1213"></span>
                  Professional photography of your experience
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1214">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1215"></span>
                  Featured in event social media channels
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1216">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1217"></span>
                  Custom branded moment at the event
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20hosting%20an%20influencer%20event%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full bg-amber-400 hover:bg-amber-500 text-black py-3 text-center text-sm tracking-wider uppercase font-medium transition-colors duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1218">
                Apply Now
              </motion.a>
            </div>
          </motion.div>

          {/* Cultural Ambassador */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-1219">
            <div className="border-2 border-amber-400/30 bg-black/80 p-8 h-full flex flex-col" data-id="element-1220">
              <div className="text-center mb-6" data-id="element-1221">
                <Globe className="w-10 h-10 text-amber-400 mx-auto mb-3" data-id="element-1222" />
                <h3 className="font-serif text-2xl text-white mb-1" data-id="element-1223">
                  Cultural Ambassador
                </h3>
                <p className="text-amber-400/60 text-xs tracking-wider uppercase font-light" data-id="element-1224">
                  Full Partnership
                </p>
                <div className="w-12 h-px bg-amber-400/30 mx-auto mt-3" data-id="element-1225"></div>
              </div>
              <ul className="space-y-3 flex-grow" data-id="element-1226">
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1227">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1228"></span>
                  All Experience Host benefits included
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1229">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1230"></span>
                  Multi-event access (May–Dec 2026)
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1231">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1232"></span>
                  Co-branded content series opportunity
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1233">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1234"></span>
                  Input into future cultural experiences
                </li>
                <li className="flex items-start text-white/70 text-sm font-light" data-id="element-1235">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mr-3 mt-1.5 flex-shrink-0" data-id="element-1236"></span>
                  Recognition as Cultural Ambassador
                </li>
              </ul>
              <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20becoming%20a%20Cultural%20Ambassador%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="mt-8 w-full border border-amber-400/50 hover:bg-amber-400 hover:text-black text-white py-3 text-center text-sm tracking-wider uppercase font-light transition-all duration-300 block" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-1237">
                Apply Now
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function ExperienceDetailsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1238">
      <div className="container mx-auto px-4" data-id="element-1239">
        <div className="max-w-5xl mx-auto" data-id="element-1240">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60" data-id="element-1241">
            <div className="text-center mb-10" data-id="element-1242">
              <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-4" data-id="element-1243">
                Event Details
              </p>
              <h2 className="font-serif text-4xl text-white mb-4" data-id="element-1244">
                The <span className="text-amber-400" data-id="element-1245">Experience</span>
              </h2>
              <div className="w-16 h-px bg-amber-400/30 mx-auto" data-id="element-1246"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10" data-id="element-1247">
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-1248">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-1249">
                  Venue
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-1250">
                  Illuminarium Toronto
                </p>
                <p className="text-white/50 text-sm font-light" data-id="element-1251">Toronto, ON</p>
              </div>
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-1252">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-1253">
                  When
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-1254">
                  Spring 2026
                </p>
                <p className="text-white/50 text-sm font-light" data-id="element-1255">
                  Monthly May–Dec 2026
                </p>
              </div>
              <div className="text-center border-l-2 border-amber-400/30 pl-6" data-id="element-1256">
                <p className="text-amber-400 text-xs tracking-wider uppercase mb-2" data-id="element-1257">
                  Format
                </p>
                <p className="font-serif text-xl text-white mb-1" data-id="element-1258">90 Minutes</p>
                <p className="text-white/50 text-sm font-light" data-id="element-1259">
                  Immersive Dining
                </p>
              </div>
            </div>
            <div className="text-center pt-8 border-t border-white/10" data-id="element-1260">
              <p className="text-xl text-amber-400 italic font-light" data-id="element-1261">
                "Step inside a living, breathing version of the Sikh Kingdoms —
                where you don't just see history, you experience it."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>;
}
function SocialProofSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1262">
      <div className="absolute inset-0 bg-gradient-to-b from-amber-950/10 to-black z-0" data-id="element-1263"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-1264">
        <div className="max-w-4xl mx-auto" data-id="element-1265">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="text-center mb-16" data-id="element-1266">
            <p className="text-amber-400/60 tracking-[0.3em] text-xs uppercase mb-6" data-id="element-1267">
              The Legacy Behind the Experience
            </p>
            <h2 className="font-serif text-5xl md:text-6xl text-white mb-8" data-id="element-1268">
              BUILT ON <span className="text-amber-400" data-id="element-1269">EXCELLENCE</span>
            </h2>
            <div className="w-24 h-px bg-amber-400/30 mx-auto" data-id="element-1270"></div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="border-2 border-amber-400/40 p-8 md:p-12 bg-gradient-to-br from-amber-950/20 to-black/60 mb-8" data-id="element-1271">
            <p className="text-2xl text-amber-400 font-light italic text-center leading-relaxed" data-id="element-1272">
              "This immersive experience honors the groundbreaking Art of the
              Sikh Kingdoms exhibition at the Royal Ontario Museum — the second
              most popular exhibition in ROM history."
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1273">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1274">
              <Award className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-1275" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-1276">
                Oscar-Winning Animators
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-1277">
                The team behind Lord of the Rings
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1278">
              <Star className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-1279" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-1280">
                Emmy-Winning Artists
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-1281">
                Creative minds behind Hasbro's global installations
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="border border-amber-400/30 p-6 text-center bg-black/40" data-id="element-1282">
              <Crown className="w-8 h-8 text-amber-400 mx-auto mb-3" data-id="element-1283" />
              <h4 className="font-serif text-lg text-white mb-2" data-id="element-1284">
                HBO Producer
              </h4>
              <p className="text-white/60 text-sm font-light" data-id="element-1285">
                Game of Thrones production pedigree
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.section>;
}
function InfluencerCTA() {
  const sectionRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  return <motion.section ref={sectionRef} style={{
    opacity
  }} className="py-20 md:py-28 bg-black relative overflow-hidden" data-id="element-1286">
      <div className="container mx-auto px-4" data-id="element-1287">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto text-center" data-id="element-1288">
          <Sparkles className="w-10 h-10 text-amber-400 mx-auto mb-6" data-id="element-1289" />
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6" data-id="element-1290">
            Ready to Create Something{' '}
            <span className="text-amber-400" data-id="element-1291">Extraordinary?</span>
          </h2>
          <div className="w-24 h-px bg-amber-400/30 mx-auto mb-8" data-id="element-1292"></div>
          <p className="text-xl text-white/70 font-light mb-10 leading-relaxed" data-id="element-1293">
            We're looking for creators who are passionate about culture,
            heritage, and authentic storytelling. Let's build something your
            audience will remember.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1294">
            <motion.a href="https://wa.me/14169998778?text=I%20am%20interested%20in%20hosting%20an%20influencer%20event%20for%20Life%20in%20the%20Sikh%20Kingdoms" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-amber-400 hover:bg-amber-500 text-black px-8 py-4 font-medium tracking-wider uppercase text-sm transition-colors duration-300" whileHover={{
            scale: 1.02
          }} whileTap={{
            scale: 0.98
          }} data-id="element-1295">
              <MessageCircle className="w-5 h-5" data-id="element-1296" />
              Apply via WhatsApp
            </motion.a>
            <Link to="/" className="inline-flex items-center justify-center gap-2 border-2 border-amber-400/50 hover:border-amber-400 text-white hover:text-amber-400 px-8 py-4 font-light tracking-wider uppercase text-sm transition-all duration-300" data-id="element-1297">
              <ArrowLeft className="w-4 h-4" data-id="element-1298" />
              Back to Main Site
            </Link>
          </div>
          <p className="text-white/40 text-sm font-light mt-6" data-id="element-1299">
            Our team will respond within 24 hours to discuss collaboration
            opportunities.
          </p>
        </motion.div>
      </div>
    </motion.section>;
}